import React, { useState } from "react";
import {
  Pencil,
  Trash2,
  Eye,
  MapPin,
  Package,
  Boxes,
  Loader,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import SearchableSelect from "../SearchableSelect";
import { masterService } from "../../api/masterService";
import { useNotification } from "../../contexts/NotificationContext";

export default function DataStockLocationTable({
  data = [],
  loading = false,
  error = null,
  currentPage = 1,
  itemsPerPage = 10,
  totalEntries = 0,
  startEntry = 1,
  endEntry = 10,
  maxPages = 1,
  onPageChange,
  onItemsPerPageChange,
  onEdit,
  onDelete,
  onDetail,
  onRefresh,
}) {
  const { showSuccess, showError } = useNotification();
  const [togglingId, setTogglingId] = useState(null);

  const handleToggleStatus = async (item) => {
    try {
      setTogglingId(item.id);
      const newStatus = !item.is_active;
      await masterService.stockLocations.toggleStatus(item.id, newStatus);
      showSuccess(
        `Lokasi stok "${item.name}" berhasil ${newStatus ? "diaktifkan" : "dinonaktifkan"}`
      );
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.message || "Gagal mengubah status lokasi stok");
    } finally {
      setTogglingId(null);
    }
  };

  const getTypeBadgeCls = (type) => {
    switch ((type || "").toLowerCase()) {
      case "gudang":
        return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
      case "toko":
        return "bg-blue-50 text-blue-700 ring-1 ring-blue-200";
      case "rak":
        return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
      case "display":
        return "bg-purple-50 text-purple-700 ring-1 ring-purple-200";
      default:
        return "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
    }
  };

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-slate-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-36">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Nama Lokasi
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Tipe Lokasi
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Deskripsi
                  </th>
                  <th className="px-6 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Jumlah Produk
                  </th>
                  <th className="px-6 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Total Stok
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Dibuat Oleh
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-12 text-center text-slate-400 text-sm">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors">
                      {/* AKSI: EDIT, HAPUS, DETAIL */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => onEdit(item)}
                            className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                            title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onDelete(item)}
                            className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                            title="Hapus">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          {onDetail && (
                            <button
                              onClick={() => onDetail(item)}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                              title="Detail">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          disabled={togglingId === item.id}
                          onClick={() => handleToggleStatus(item)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                            item.is_active
                              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100"
                              : "bg-rose-50 text-rose-600 ring-1 ring-rose-200 hover:bg-rose-100"
                          }`}>
                          {togglingId === item.id ? (
                            <Loader className="w-3 h-3 animate-spin text-rose-500" />
                          ) : item.is_active ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-rose-400" />
                          )}
                          <span>
                            {item.is_active ? "Aktif" : "Non-Aktif"}
                          </span>
                        </button>
                      </td>

                      {/* NAMA LOKASI */}
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        <div className="flex items-center gap-2.5">
                          {item.image_url && item.image_url.trim() !== "" ? (
                            <div className="w-10 h-7.5 rounded-lg overflow-hidden border border-slate-200 shrink-0 bg-slate-100 flex items-center justify-center">
                              <img
                                src={item.image_url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                              <MapPin className="w-4 h-4" />
                            </div>
                          )}
                          <span>{item.name}</span>
                        </div>
                      </td>

                      {/* TIPE LOKASI */}
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${getTypeBadgeCls(item.type)}`}>
                          {item.type || "Gudang"}
                        </span>
                      </td>

                      {/* DESKRIPSI */}
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                        {item.description || "-"}
                      </td>

                      {/* JUMLAH PRODUK */}
                      <td className="px-6 py-4 text-sm text-center font-medium text-slate-700">
                        <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-md text-xs">
                          <Package className="w-3.5 h-3.5 text-slate-400" />
                          {item.product_count || 0} Produk
                        </span>
                      </td>

                      {/* TOTAL STOK */}
                      <td className="px-6 py-4 text-sm text-center font-semibold text-emerald-600">
                        <span className="inline-flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-md text-xs text-emerald-700 ring-1 ring-emerald-200">
                          <Boxes className="w-3.5 h-3.5 text-emerald-500" />
                          {item.total_stock || 0}
                        </span>
                      </td>

                      {/* DIBUAT OLEH */}
                      <td className="px-6 py-4 text-xs text-slate-700 font-medium">
                        {item.created_by_name || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination (Match DataProductTable Exactly) */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-medium text-slate-700">{startEntry}</span>{" "}
                to{" "}
                <span className="font-medium text-slate-700">{endEntry}</span>{" "}
                of{" "}
                <span className="font-medium text-slate-700">
                  {totalEntries}
                </span>{" "}
                entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(val) => onItemsPerPageChange && onItemsPerPageChange(Number(val))}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onPageChange && onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = Math.max(1, maxPages - 2);
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange && onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange && onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages || maxPages === 0}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ›
              </button>
              <button
                onClick={() => onPageChange && onPageChange(maxPages)}
                disabled={currentPage === maxPages || maxPages === 0}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
